import * as React from 'react';

import { DescriptionPage_ProductVariant as ProductVariant } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import { LoadingSection } from '@coursera/cds-core';

import { NotFound } from 'bundles/common/components/withNotFound';
import { useSpecializationReviewsPageQuery } from 'bundles/reviews/hooks/useSpecializationReviewsPageQuery';
import SpecializationReviewsPageContent from 'bundles/reviews/pages/specialization/SpecializationReviewsPageContent';
import { guardSpecializationReviewsPage } from 'bundles/reviews/utils/guardSpecializationReviewsPage';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useSpecializationReviewsExperimentConfig } from 'bundles/unified-description-page-common/providers/specialization/SpecializationReviewsExperimentProvider';

type Props = {
  productVariant?: ProductVariant;
};

/**
 * `productVariant` comes from the route (StandardSpecialization vs ProfessionalCertificate), not
 * the page data query, so the redirect guard below can run before that query resolves.
 */
const SpecializationReviewsPageGuard = ({ productVariant = ProductVariant.StandardSpecialization }: Props) => {
  const params = useParams();
  const slug = params?.slug ?? '';
  const programSlug = params?.programSlug;
  const orgSlug = params?.orgSlug;

  const { showReviews } = useSpecializationReviewsExperimentConfig();
  guardSpecializationReviewsPage(showReviews, { slug, productVariant, orgSlug, programSlug });

  const { loading, error, reviewsPageData } = useSpecializationReviewsPageQuery(!showReviews);

  if (loading) {
    return <LoadingSection />;
  }

  if (!slug || error || !reviewsPageData?.name) {
    if (inServerContext) {
      throw new NotFoundError();
    }
    return <NotFound />;
  }

  return (
    <SpecializationReviewsPageContent
      reviewsPageData={reviewsPageData}
      productVariant={productVariant}
      slug={slug}
      programSlug={programSlug}
      orgSlug={orgSlug}
    />
  );
};

export default SpecializationReviewsPageGuard;
